import { Money } from '@klear/money'
import { KLEAR_SUPPORT_EMAIL } from '@klear/constants/email'
import { Document, Page, Text, View, Image } from '@react-pdf/renderer'
import klearLogo from '@/assets/logo-klear.png'
import type { KlearFinancialAccount } from '@klear/klear-financial-account/klear-financial-account'
import type { Organization } from '@klear/organization'
import { KlearDate } from '@klear/date'
import styles from '@/components/pdf/pdf-styles'
import type { MonthlyStatement } from '@/hooks/use-monthly-statements'

type TransactionDisplayData = {
  date: KlearDate | null
  description: string
  type: string
  amount: Money
  balanceAfter: Money
  isOutbound: boolean
}

interface MonthlyAccountStatementsDocumentProps {
  monthlyStatement: MonthlyStatement
  klearFinancialAccount: KlearFinancialAccount
  organization: Organization
}

const KlearAccountMonthlyStatementDocument = ({
  monthlyStatement,
  klearFinancialAccount,
  organization
}: MonthlyAccountStatementsDocumentProps) => {
  const companyName = klearFinancialAccount.organization.name
  const routingNumber = klearFinancialAccount.routingNumber
  const accountNumber = klearFinancialAccount.accountNumber
  const line1 = organization.address?.line1
  const line2 = organization.address?.line2
  const city = organization.address?.city
  const state = organization.address?.state
  const postalCode = organization.address?.postalCode
  const month = monthlyStatement.monthDate.toString('MMMM')
  const year = monthlyStatement.monthDate.toString('YYYY')
  const einDigits = organization.ein.replace(/\D/g, '')
  const einLast4 = einDigits.length >= 4 ? einDigits.slice(-4) : ''
  const firstDayOfMonth = '1'
  const lastDayOfMonth = monthlyStatement.monthDate.daysInMonth().toString()
  const daysInMonth = parseInt(lastDayOfMonth)
  const accountType = klearFinancialAccount.last4

  const beginningBalance = monthlyStatement.monthlyStartingBalance
  const totalOutBoundTransfers = monthlyStatement.totalOutflow
  const totalInboundTransfers = monthlyStatement.totalInflow
  const statementBalance = monthlyStatement.monthlyEndingBalance

  const transactions: TransactionDisplayData[] = monthlyStatement.transactions.map((transactionWithBalance) => {
    const transaction = transactionWithBalance.transaction
    const statementDate = transaction.postedAt ?? transaction.createdAt
    return {
      date: statementDate?.toKlearDate() ?? null,
      description: transaction.description || 'Transaction',
      type: transaction.typeLabel || 'Transfer',
      amount: transaction.amount.isNegative()
        ? transaction.amount.zero.subtract(transaction.amount)
        : transaction.amount,
      balanceAfter: transactionWithBalance.balanceAfter,
      isOutbound: transaction.isOutbound
    }
  })

  const TRANSACTIONS_PER_FIRST_PAGE = 5
  const TRANSACTIONS_PER_SUBSEQUENT_PAGE = 14

  const createTransactionPages = () => {
    const pages = []
    let currentIndex = 0

    if (transactions.length > 0) {
      const firstPageTransactions = transactions.slice(0, TRANSACTIONS_PER_FIRST_PAGE)
      pages.push({
        transactions: firstPageTransactions,
        isFirstPage: true,
        pageNumber: 1
      })
      currentIndex = TRANSACTIONS_PER_FIRST_PAGE
    }

    while (currentIndex < transactions.length) {
      const pageTransactions = transactions.slice(currentIndex, currentIndex + TRANSACTIONS_PER_SUBSEQUENT_PAGE)
      pages.push({
        transactions: pageTransactions,
        isFirstPage: false,
        pageNumber: pages.length + 1
      })
      currentIndex += TRANSACTIONS_PER_SUBSEQUENT_PAGE
    }

    return pages
  }

  const transactionPages = createTransactionPages()

  const renderTransactionTable = (pageTransactions: TransactionDisplayData[]) => (
    <View style={styles.table}>
      <View style={styles.tableRow}>
        <View style={styles.tableColHeader}>
          <Text style={styles.tableCellHeader}>Date (UTC)</Text>
        </View>
        <View style={styles.tableColHeader}>
          <Text style={styles.tableCellHeader}>Description</Text>
        </View>
        <View style={styles.tableColHeader}>
          <Text style={styles.tableCellHeader}>Type</Text>
        </View>
        <View style={styles.tableColHeader}>
          <Text style={styles.tableCellHeader}>Amount</Text>
        </View>
        <View style={styles.tableColHeader}>
          <Text style={styles.tableCellHeader}>Ending Balance</Text>
        </View>
      </View>

      {pageTransactions.map((transaction, index) => (
        <View key={index} style={styles.tableRow}>
          <View style={styles.tableCol}>
            <Text style={[styles.tableCell]}>{transaction.date?.toString() ?? '-'}</Text>
          </View>
          <View style={styles.tableCol}>
            <Text style={[styles.tableCellDescription, styles.textCenter]}>{transaction.description}</Text>
          </View>
          <View style={styles.tableCol}>
            <Text style={[styles.tableCell, styles.textCenter]}>
              {transaction.isOutbound ? '-> ' : '<- '} {transaction.type}
            </Text>
          </View>
          <View style={styles.tableCol}>
            <Text style={[styles.tableCellAmount, styles.textCenter]}>
              {(transaction.amount.isNegative()
                ? transaction.amount.zero.subtract(transaction.amount)
                : transaction.amount
              ).toString()}
            </Text>
          </View>
          <View style={styles.tableCol}>
            <Text style={[styles.tableCellBalance, styles.textCenter]}>{transaction.balanceAfter.toString()}</Text>
          </View>
        </View>
      ))}
    </View>
  )

  const renderFooter = () => (
    <View style={styles.padding14}>
      <View
        style={[
          styles.container,
          styles.padding14,
          styles.flexCol,
          styles.justifyCenter,
          styles.textCenter,
          styles.padding14,
          styles.smallText
        ]}
      >
        <Text style={styles.padding14}>
          Klear partners with <Text style={styles.textUnderline}>Stripe Payments Company</Text> for money transmission
          services and account services with funds held at Fith Third Bank N.A., Member FDIC
        </Text>
        <Text style={styles.padding14}>
          Klear | {KLEAR_SUPPORT_EMAIL} | 2021 Fillmore Street, San Francisco, CA 94115
        </Text>
      </View>
    </View>
  )

  return (
    <Document>
      {transactionPages.map((page, pageIndex) => (
        <Page key={pageIndex} size="A4" style={{ padding: 16 }}>
          {page.isFirstPage && (
            <>
              <View style={styles.textSize}>
                <View style={[styles.container, styles.padding14, styles.flexBetween, styles.itemsCenter]}>
                  <Image src={klearLogo} style={{ width: 90, height: 30 }} />
                  <View style={[styles.lightText, styles.textColumn, styles.smallText]}>
                    <Text>
                      {month} {year} Statement
                    </Text>
                    <Text>
                      {month} {firstDayOfMonth}, {year} - {month} {lastDayOfMonth}, {year} ({daysInMonth})
                    </Text>
                    <Text>All dates in UTC</Text>
                  </View>
                </View>
              </View>

              <View style={styles.spacer} />
              <View style={[styles.flexBetween, styles.gap8]}>
                <View style={[styles.container, styles.padding14, styles.width45]}>
                  <View style={[styles.textColumn, styles.marginTop24]}>
                    <Text style={[styles.textBold, styles.textLarge]}>{companyName}</Text>
                  </View>
                  <View style={[styles.textColumn, styles.marginTop16, styles.smallText]}>
                    <Text>{line1}</Text>
                    {line2 && <Text>{line2}</Text>}
                    {city && state && postalCode ? (
                      <Text>
                        {city}, {state} {postalCode}
                      </Text>
                    ) : null}
                  </View>
                  <View style={[styles.textColumn, styles.marginTop16, styles.smallText]}>
                    {einLast4 ? <Text>EIN **{einLast4}</Text> : null}
                  </View>
                </View>
                <View style={[styles.container, styles.padding14, styles.width55]}>
                  <View style={[styles.textColumn, styles.marginTop24]}>
                    <Text style={[styles.textBold, styles.textLarge, styles.textBlue]}>Account Details</Text>
                  </View>
                  <View style={[styles.detailRow, styles.marginTop12, styles.smallText]}>
                    <Text style={styles.fixedWidthRow}>Account Type</Text>
                    <Text>{accountType}</Text>
                  </View>
                  <View style={[styles.detailRow, styles.marginTop12, styles.smallText]}>
                    <Text style={styles.fixedWidthRow}>Account Number</Text>
                    <Text>{accountNumber}</Text>
                  </View>
                  <View style={[styles.detailRow, styles.marginTop12, styles.smallText]}>
                    <Text style={styles.fixedWidthRow}>Routing Number</Text>
                    <Text>{routingNumber}</Text>
                  </View>
                  <View style={[styles.textColumn, styles.marginTop24]}>
                    <Text style={[styles.textBold, styles.textLarge, styles.textBlue]}>Account Details</Text>
                  </View>
                  <View style={[styles.detailRow, styles.marginTop12, styles.smallText]}>
                    <Text style={styles.fixedWidthRow}>Beginning Balance</Text>
                    <Text>{beginningBalance.toString()}</Text>
                  </View>
                  <View style={[styles.detailRow, styles.marginTop12, styles.smallText]}>
                    <Text style={styles.fixedWidthRow}>Total Outbound Transfers</Text>
                    <Text>{totalOutBoundTransfers.toString()}</Text>
                  </View>
                  <View style={[styles.detailRow, styles.marginTop12, styles.smallText]}>
                    <Text style={styles.fixedWidthRow}>Total Inbound Transfers</Text>
                    <Text>{totalInboundTransfers.toString()}</Text>
                  </View>
                  <View style={[styles.detailRow, styles.marginTop12, styles.smallText]}>
                    <Text style={styles.fixedWidthRow}>Ending Balance</Text>
                    <Text>{statementBalance.toString()}</Text>
                  </View>
                </View>
              </View>
            </>
          )}

          {page.isFirstPage && (
            <View style={[styles.container, styles.marginTop24]}>
              <View style={[styles.textColumn, styles.marginTop24, styles.padding14]}>
                <Text style={[styles.textBold, styles.textLarge, styles.textBlue]}>All Transactions</Text>
              </View>
              <View style={styles.spacer} />
            </View>
          )}
          {renderTransactionTable(page.transactions)}
          {pageIndex === transactionPages.length - 1 && (
            <>
              <View style={styles.spacer} />
              <View style={styles.spacer} />
              {renderFooter()}
            </>
          )}
        </Page>
      ))}
    </Document>
  )
}

export default KlearAccountMonthlyStatementDocument
